/**
 * Error logging and reporting utility
 * Provides centralized error handling, logging, and reporting functionality
 */

export interface ErrorContext {
  component?: string
  action?: string
  userId?: string
  sessionId?: string
  timestamp?: string
  url?: string
  userAgent?: string
  [key: string]: any
}

export interface ErrorReport {
  id: string
  error: {
    name: string
    message: string
    stack?: string
  }
  context: ErrorContext
  timestamp: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  reported: boolean
}

class ErrorLogger {
  private errors: ErrorReport[] = []
  private maxErrors = 100 // Keep last 100 errors in memory
  private reportingEndpoint = '/api/errors' // Would be configured based on environment

  /**
   * Log an error with context information
   */
  logError(error: Error, context: ErrorContext = {}): string {
    const errorId = this.generateErrorId()
    const severity = this.determineSeverity(error, context)
    
    const errorReport: ErrorReport = {
      id: errorId,
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      context: {
        ...context,
        timestamp: context.timestamp || new Date().toISOString(),
        url: context.url || window.location.href,
        userAgent: context.userAgent || navigator.userAgent
      },
      timestamp: new Date().toISOString(),
      severity,
      reported: false
    }

    // Add to in-memory storage
    this.errors.unshift(errorReport)
    
    // Keep only the most recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors)
    }

    // Store in localStorage for persistence across sessions
    this.persistError(errorReport)

    // Log to console for development
    if (import.meta.env.DEV) {
      console.group(`🚨 Error Logged [${severity.toUpperCase()}]`)
      console.error('Error:', error)
      console.log('Context:', context)
      console.log('Error ID:', errorId)
      console.groupEnd()
    }

    // Auto-report critical errors
    if (severity === 'critical') {
      this.reportError(error, context)
    }

    return errorId
  }

  /**
   * Report an error to the backend service
   */
  async reportError(error: Error, context: ErrorContext = {}): Promise<boolean> {
    try {
      const errorReport = this.errors.find(e => 
        e.error.message === error.message && 
        e.error.name === error.name
      )

      if (!errorReport) {
        // Create a new error report if not found
        const errorId = this.logError(error, context)
        const newReport = this.errors.find(e => e.id === errorId)
        if (newReport) {
          return this.sendErrorReport(newReport)
        }
        return false
      }

      return this.sendErrorReport(errorReport)
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError)
      return false
    }
  }

  /**
   * Get all logged errors
   */
  getErrors(): ErrorReport[] {
    return [...this.errors]
  }

  /**
   * Get errors by severity
   */
  getErrorsBySeverity(severity: ErrorReport['severity']): ErrorReport[] {
    return this.errors.filter(error => error.severity === severity)
  }

  /**
   * Clear all logged errors
   */
  clearErrors(): void {
    this.errors = []
    localStorage.removeItem('app_error_logs')
  }

  /**
   * Get error statistics
   */
  getErrorStats(): {
    total: number
    bySeverity: Record<string, number>
    reported: number
    unreported: number
  } {
    const stats = {
      total: this.errors.length,
      bySeverity: {
        low: 0,
        medium: 0,
        high: 0,
        critical: 0
      },
      reported: 0,
      unreported: 0
    }

    this.errors.forEach(error => {
      stats.bySeverity[error.severity]++
      if (error.reported) {
        stats.reported++
      } else {
        stats.unreported++
      }
    })

    return stats
  }

  /**
   * Initialize error logger - load persisted errors
   */
  initialize(): void {
    this.loadPersistedErrors()
    
    // Set up periodic cleanup
    setInterval(() => {
      this.cleanupOldErrors()
    }, 60000) // Clean up every minute
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private determineSeverity(error: Error, context: ErrorContext): ErrorReport['severity'] {
    // Critical errors
    if (
      error.name === 'ChunkLoadError' ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      context.component === 'App' ||
      error.message.includes('Network Error') && context.action === 'authentication'
    ) {
      return 'critical'
    }

    // High severity errors
    if (
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network request failed') ||
      error.message.includes('Authentication') ||
      error.message.includes('Authorization') ||
      error.name === 'TypeError' && error.message.includes('Cannot read')
    ) {
      return 'high'
    }

    // Medium severity errors
    if (
      error.message.includes('Validation') ||
      error.message.includes('Conflict') ||
      error.message.includes('not found') ||
      error.name === 'ValidationError'
    ) {
      return 'medium'
    }

    // Default to low severity
    return 'low'
  }

  private persistError(errorReport: ErrorReport): void {
    try {
      const existingErrors = this.getPersistedErrors()
      existingErrors.unshift(errorReport)
      
      // Keep only recent errors in localStorage
      const recentErrors = existingErrors.slice(0, 50)
      localStorage.setItem('app_error_logs', JSON.stringify(recentErrors))
    } catch (e) {
      console.warn('Failed to persist error to localStorage:', e)
    }
  }

  private loadPersistedErrors(): void {
    try {
      const persistedErrors = this.getPersistedErrors()
      this.errors = [...persistedErrors, ...this.errors]
    } catch (e) {
      console.warn('Failed to load persisted errors:', e)
    }
  }

  private getPersistedErrors(): ErrorReport[] {
    try {
      const stored = localStorage.getItem('app_error_logs')
      return stored ? JSON.parse(stored) : []
    } catch (e) {
      return []
    }
  }

  private async sendErrorReport(errorReport: ErrorReport): Promise<boolean> {
    try {
      // In a real application, this would send to your error reporting service
      // For now, we'll simulate the API call
      
      if (import.meta.env.DEV) {
        console.log('📤 Simulating error report:', errorReport)
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Mark as reported
        errorReport.reported = true
        this.persistError(errorReport)
        
        return true
      }

      // Production error reporting would go here
      // const response = await fetch(this.reportingEndpoint, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify(errorReport)
      // })
      
      // if (response.ok) {
      //   errorReport.reported = true
      //   this.persistError(errorReport)
      //   return true
      // }
      
      return false
    } catch (e) {
      console.error('Error reporting failed:', e)
      return false
    }
  }

  private cleanupOldErrors(): void {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
    
    this.errors = this.errors.filter(error => 
      new Date(error.timestamp) > oneDayAgo
    )
    
    // Update localStorage
    try {
      localStorage.setItem('app_error_logs', JSON.stringify(this.errors.slice(0, 50)))
    } catch (e) {
      console.warn('Failed to update persisted errors:', e)
    }
  }
}

// Export singleton instance
export const errorLogger = new ErrorLogger()

// Initialize on module load
errorLogger.initialize()

// Helper functions for common error scenarios
export const logNetworkError = (error: Error, endpoint: string, method: string = 'GET') => {
  return errorLogger.logError(error, {
    type: 'network',
    endpoint,
    method,
    action: 'api_request'
  })
}

export const logValidationError = (error: Error, field: string, value: any) => {
  return errorLogger.logError(error, {
    type: 'validation',
    field,
    value: typeof value === 'object' ? JSON.stringify(value) : String(value),
    action: 'form_validation'
  })
}

export const logComponentError = (error: Error, componentName: string, props?: any) => {
  return errorLogger.logError(error, {
    type: 'component',
    component: componentName,
    props: props ? JSON.stringify(props) : undefined,
    action: 'component_render'
  })
}

export const logServiceError = (error: Error, serviceName: string, methodName: string, params?: any) => {
  return errorLogger.logError(error, {
    type: 'service',
    service: serviceName,
    method: methodName,
    params: params ? JSON.stringify(params) : undefined,
    action: 'service_call'
  })
}